import React from 'react';
import './TemplateStepsEditor.css';
import Textfield from '../input/Textfield';
import { ParticipantGroupChip } from '../participant-group/ParticipantGroupChips';
import TemplateIconField from './TemplateIconField';
import TemplateTextblockSelect from './TemplateTextblockSelect';
import { compareParticipantGroups } from '../../util/participantGroups';
import {
  NEWSLETTER_TARGET_LABEL, STEP_KINDS, STEP_KIND_HINT, STEP_KIND_ICON, STEP_KIND_LABEL,
  isReleaseTargetGroup, newStep, stepIssues, templateWarnings,
} from './templateModel';

// Pseudo group for the newsletter chip (no group color → empty ring).
const NEWSLETTER_CHIP = { id: 'newsletter', name: NEWSLETTER_TARGET_LABEL, colorKey: 'neutral', isArchived: false };

// Group targets in display order, newsletter last — the request keeps this order.
function sortTargets(targets, groupsById) {
  const order = (t) => {
    if (t.targetKind === 'Newsletter') return [2, null];
    const g = groupsById.get(Number(t.participantGroupId));
    return g ? [0, g] : [1, null];
  };
  return [...targets].sort((a, b) => {
    const [ra, ga] = order(a);
    const [rb, gb] = order(b);
    if (ra !== rb) return ra - rb;
    return ga && gb ? compareParticipantGroups(ga, gb) : 0;
  });
}

const sameTarget = (a, b) => a.targetKind === b.targetKind
  && (a.targetKind === 'Newsletter' || Number(a.participantGroupId) === Number(b.participantGroupId));

// Offered groups + groups that are selected although not offered (archived, wrong kind) so they can be
// deselected; in display order.
function chipGroups(offered, selectedIds, groupsById) {
  const ids = new Set(offered.map(g => g.id));
  const extra = [...selectedIds]
    .filter(id => !ids.has(id))
    .map(id => groupsById.get(id))
    .filter(Boolean);
  return [...offered, ...extra].sort(compareParticipantGroups);
}

function ReleaseTargets({ step, rules, groups, groupsById, onChangeTargets }) {
  const selectedIds = new Set(step.targets.filter(t => t.targetKind === 'ParticipantGroup').map(t => Number(t.participantGroupId)));
  const offered = groups.filter(g => !g.isArchived && isReleaseTargetGroup(g));
  const formGroups = groups.filter(g => !g.isArchived && !g.isGuest && !isReleaseTargetGroup(g));
  const guestName = groups.find(g => g.isGuest)?.pluralName || 'Gäste';

  const toggle = (id) => {
    const next = selectedIds.has(id)
      ? step.targets.filter(t => !(t.targetKind === 'ParticipantGroup' && Number(t.participantGroupId) === id))
      : [...step.targets, { targetKind: 'ParticipantGroup', participantGroupId: id, textblockId: null }];
    onChangeTargets(sortTargets(next, groupsById));
  };

  return (
    <div className="ptStepTargets">
      <span className="ptStepTargetsLabel">Freigeben für</span>
      <div className="participantGroupChips" role="group" aria-label={`Freigabe-Gruppen für ${step.label || 'diesen Schritt'}`}>
        {chipGroups(offered, selectedIds, groupsById).map(g => {
          const selected = selectedIds.has(g.id);
          const locked = g.isGuest && rules.neverPublic;
          const notOffered = g.isArchived || !isReleaseTargetGroup(g);
          // Selected chips stay clickable so a conflicting target can be removed.
          const disabled = !selected && (locked || notOffered);
          const title = locked
            ? `Events dieser Vorlage sind nie öffentlich – keine Freigabe für „${guestName}“.`
            : undefined;
          return (
            <ParticipantGroupChip
              key={g.id}
              group={g}
              label={g.pluralName || g.name}
              active={selected}
              disabled={disabled}
              title={title}
              onClick={() => toggle(g.id)}
            />
          );
        })}
      </div>
      {rules.neverPublic && (
        <p className="ptStepTargetHint">„{guestName}“ gesperrt: Events dieser Vorlage sind nie öffentlich.</p>
      )}
      {formGroups.length > 0 && (
        <p className="ptStepTargetHint">
          {formGroups.map(g => `„${g.name}“`).join(', ')} {formGroups.length === 1 ? 'meldet' : 'melden'} über
          das Formular an und {formGroups.length === 1 ? 'sieht' : 'sehen'}, was Gäste sehen – dafür „{guestName}“ freigeben.
        </p>
      )}
    </div>
  );
}

function MailTargets({ step, rules, groups, groupsById, textblocks, onChangeTargets }) {
  const groupTargets = step.targets.filter(t => t.targetKind === 'ParticipantGroup');
  const selectedIds = new Set(groupTargets.map(t => Number(t.participantGroupId)));
  const newsletterSelected = step.targets.some(t => t.targetKind === 'Newsletter');
  // Guests are reached via the newsletter (consent), never as a player group.
  const offered = groups.filter(g => !g.isArchived && !g.isGuest);

  const toggleGroup = (id) => {
    const next = selectedIds.has(id)
      ? step.targets.filter(t => !(t.targetKind === 'ParticipantGroup' && Number(t.participantGroupId) === id))
      : [...step.targets, { targetKind: 'ParticipantGroup', participantGroupId: id, textblockId: null }];
    onChangeTargets(sortTargets(next, groupsById));
  };

  const toggleNewsletter = () => {
    const next = newsletterSelected
      ? step.targets.filter(t => t.targetKind !== 'Newsletter')
      : [...step.targets, { targetKind: 'Newsletter', participantGroupId: null, textblockId: null }];
    onChangeTargets(sortTargets(next, groupsById));
  };

  const setTextblock = (target, textblockId) => {
    onChangeTargets(step.targets.map(t => (sameTarget(t, target) ? { ...t, textblockId } : t)));
  };

  const newsletterLocked = rules.neverPublic && !newsletterSelected;

  return (
    <div className="ptStepTargets">
      <span className="ptStepTargetsLabel">Mail an</span>
      <div className="participantGroupChips" role="group" aria-label={`Mail-Ziele für ${step.label || 'diesen Schritt'}`}>
        {chipGroups(offered, selectedIds, groupsById).map(g => {
          const selected = selectedIds.has(g.id);
          const notOffered = g.isArchived || g.isGuest;
          return (
            <ParticipantGroupChip
              key={g.id}
              group={g}
              label={g.pluralName || g.name}
              active={selected}
              disabled={!selected && notOffered}
              onClick={() => toggleGroup(g.id)}
            />
          );
        })}
        <ParticipantGroupChip
          group={NEWSLETTER_CHIP}
          active={newsletterSelected}
          disabled={newsletterLocked}
          title={newsletterLocked ? 'Events dieser Vorlage sind nie öffentlich – kein Newsletter.' : 'Abonnenten des Newsletters (mit Einwilligung)'}
          onClick={toggleNewsletter}
        />
      </div>
      <p className="ptStepTargetHint">
        Gäste erreichst du über „{NEWSLETTER_TARGET_LABEL}“.
        {rules.neverPublic && ' Bei dieser Vorlage ist der Newsletter gesperrt (nie öffentlich).'}
      </p>

      {step.targets.length > 0 && (
        <ul className="ptMailTargetList">
          {step.targets.map(t => {
            const isNewsletter = t.targetKind === 'Newsletter';
            const g = isNewsletter ? NEWSLETTER_CHIP : groupsById.get(Number(t.participantGroupId));
            return (
              <li key={isNewsletter ? 'newsletter' : `g${t.participantGroupId}`} className="ptMailTargetRow">
                <span className="ptMailTargetName">
                  {g
                    ? <ParticipantGroupChip group={g} label={isNewsletter ? 'Newsletter' : (g.pluralName || g.name)} />
                    : 'Unbekannte Gruppe'}
                </span>
                {/* Group targets may inherit the group's "Ankündigung" rule; the newsletter has no
                    rule and needs its own textblock (server validator). */}
                <TemplateTextblockSelect
                  label="Textbaustein"
                  value={t.textblockId}
                  textblocks={textblocks}
                  emptyLabel={isNewsletter ? 'Bitte Textbaustein wählen …' : 'Aus der Mail-Regel „Ankündigung“ der Gruppe'}
                  required={isNewsletter}
                  onChange={id => setTextblock(t, id)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function StepCard({ step, index, count, rules, groups, groupsById, textblocks, onPatch, onMove, onRemove }) {
  const issues = groupsById.size > 0 ? stepIssues(step, rules, groupsById) : [];
  const label = step.label.trim() || `Schritt ${index + 1}`;

  return (
    <li className={`ptStep${issues.length > 0 ? ' hasIssues' : ''}`}>
      <div className="ptStepHead">
        <span className="ptStepNumber" aria-hidden="true">{index + 1}</span>
        <span className="ptStepKind">
          <span className="material-symbols-rounded" aria-hidden="true">{STEP_KIND_ICON[step.kind]}</span>
          {STEP_KIND_LABEL[step.kind]}
        </span>
        <span className="ptStepTools">
          <button
            type="button"
            className="ptStepBtn"
            onClick={() => onMove(index, -1)}
            disabled={index === 0}
            aria-label={`${label} nach oben`}
            title="Nach oben"
          >
            <span className="material-symbols-rounded" aria-hidden="true">arrow_upward</span>
          </button>
          <button
            type="button"
            className="ptStepBtn"
            onClick={() => onMove(index, 1)}
            disabled={index === count - 1}
            aria-label={`${label} nach unten`}
            title="Nach unten"
          >
            <span className="material-symbols-rounded" aria-hidden="true">arrow_downward</span>
          </button>
          <button
            type="button"
            className="ptStepBtn isDanger"
            onClick={() => onRemove(step.key)}
            aria-label={`${label} entfernen`}
            title="Schritt entfernen"
          >
            <span className="material-symbols-rounded" aria-hidden="true">delete</span>
          </button>
        </span>
      </div>

      <p className="ptStepHint">{STEP_KIND_HINT[step.kind]}</p>

      <div className="ptStepFields">
        <Textfield
          label="Bezeichnung"
          value={step.label}
          maxLength={100}
          placeholder={STEP_KIND_LABEL[step.kind]}
          onChange={e => onPatch(step.key, { label: e.target.value })}
        />
        <TemplateIconField
          label="Icon"
          ariaLabel={`Icon für ${label}`}
          value={step.icon}
          fallbackIcon={STEP_KIND_ICON[step.kind]}
          onChange={icon => onPatch(step.key, { icon })}
        />
      </div>

      {step.kind === 'Release' && (
        <ReleaseTargets
          step={step}
          rules={rules}
          groups={groups}
          groupsById={groupsById}
          onChangeTargets={targets => onPatch(step.key, { targets })}
        />
      )}
      {step.kind === 'SendMail' && (
        <MailTargets
          step={step}
          rules={rules}
          groups={groups}
          groupsById={groupsById}
          textblocks={textblocks}
          onChangeTargets={targets => onPatch(step.key, { targets })}
        />
      )}

      {issues.length > 0 && (
        <ul className="ptStepIssues">
          {issues.map((text, i) => (
            <li key={i}>
              <span className="material-symbols-rounded" aria-hidden="true">error</span>
              {text}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * Steps tab of the template editor: ordered list (up/down/remove), add by kind, label + icon per
 * step, targets for Release (group chips) and SendMail (groups + newsletter, textblock per target).
 * Inline hints mirror the server validator; the server stays authoritative on save.
 */
function TemplateStepsEditor({ form, onChangeSteps, groups, groupsById, groupsLoaded, textblocks }) {
  const steps = form.steps;
  const rules = { neverPublic: form.neverPublic, requiresLeader: form.requiresLeader };
  const warnings = templateWarnings(form);

  const patchStep = (key, changes) => onChangeSteps(steps.map(s => (s.key === key ? { ...s, ...changes } : s)));
  const removeStep = (key) => onChangeSteps(steps.filter(s => s.key !== key));
  const moveStep = (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[index], next[j]] = [next[j], next[index]];
    onChangeSteps(next);
  };
  const addStep = (kind) => onChangeSteps([...steps, newStep(kind)]);

  return (
    <div className="ptSteps">
      <p className="formFieldHint">
        Die Reihenfolge ist der Ablauf im Prozess. Beim Anlegen eines Prozesses werden die Schritte kopiert und
        sind dort weiter anpassbar – laufende Prozesse ändern sich nicht.
      </p>

      {!groupsLoaded && <p className="ptStepsMessage">Gruppen werden geladen …</p>}

      {steps.length === 0 ? (
        <p className="ptStepsMessage">Noch keine Schritte. Ohne Schritte werden die Events im Prozess nur angelegt.</p>
      ) : (
        <ol className="ptStepList">
          {steps.map((s, i) => (
            <StepCard
              key={s.key}
              step={s}
              index={i}
              count={steps.length}
              rules={rules}
              groups={groups}
              groupsById={groupsById}
              textblocks={textblocks}
              onPatch={patchStep}
              onMove={moveStep}
              onRemove={removeStep}
            />
          ))}
        </ol>
      )}

      <div className="ptStepAdd">
        <span className="ptStepAddLabel">Schritt hinzufügen</span>
        <div className="ptStepAddButtons">
          {STEP_KINDS.map(kind => (
            <button key={kind} type="button" className="ptStepAddBtn" onClick={() => addStep(kind)}>
              <span className="material-symbols-rounded" aria-hidden="true">{STEP_KIND_ICON[kind]}</span>
              {STEP_KIND_LABEL[kind]}
            </button>
          ))}
        </div>
      </div>

      {warnings.length > 0 && (
        <ul className="ptStepWarnings" aria-label="Hinweise">
          {warnings.map(text => (
            <li key={text}>
              <span className="material-symbols-rounded" aria-hidden="true">info</span>
              {text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default TemplateStepsEditor;
